/**
 * Pure Club program math shared by the wizard's live example sentence and
 * any surface that previews earn/redeem numbers. Mirrors the backend rules
 * (proportional floor on earn, linear on redeem).
 *
 * CLUB_EARN_RULE_MIRROR — keep `pointsForSpend` in sync with -api
 * `src/club/club-earn.utils.ts` (`earnPointsForBasis`) and -app
 * `shared/utils/club.utils.ts` (`clubPointsForSpend`); the three specs share
 * the same golden vectors.
 */

export interface IClubEarnConfig {
  earnUnitAmount: number;
  earnPoints: number;
}

export interface IClubRedeemConfig {
  redeemPointsCost: number;
  redeemValue: number;
}

/**
 * Points earned for a given spend: proportional to the spend, floored to
 * whole points, computed in integer cents so the division is exact (exact
 * while spendCents * points < 2^53). NaN or non-positive inputs yield 0.
 */
export function pointsForSpend(config: IClubEarnConfig, spend: number): number {
  const unitCents = Math.round(config.earnUnitAmount * 100);
  const spendCents = Math.round(spend * 100);
  if (!(unitCents > 0) || !(config.earnPoints > 0) || !(spendCents > 0)) {
    return 0;
  }
  return Math.floor((spendCents * config.earnPoints) / unitCents);
}

/** Money value of a points redemption at the program's rate. */
export function valueForPoints(
  config: IClubRedeemConfig,
  points: number
): number {
  if (!(config.redeemPointsCost > 0) || !(config.redeemValue > 0)) return 0;
  if (!(points > 0)) return 0;
  return (
    Math.round(
      ((points * config.redeemValue) / config.redeemPointsCost) * 100
    ) / 100
  );
}

/**
 * Representative spend for the wizard's live example: two and a half earn
 * units — round money for every currency anchor, and a fractional block so
 * the proportional rule is visible.
 */
export function exampleSpend(config: IClubEarnConfig): number {
  if (!(config.earnUnitAmount > 0)) return 0;
  return round2(config.earnUnitAmount * 2.5);
}

/**
 * Effective return of the program as a percentage of spend, combining the
 * earn and redeem rates (e.g. 100 pts per $1,000 and 100 pts = $100 → 10).
 * One decimal; 0 when any rate is invalid. Display only (never sent to the
 * API).
 */
export function effectiveReturnPercent(
  earn: IClubEarnConfig,
  redeem: IClubRedeemConfig
): number {
  if (
    !(earn.earnUnitAmount > 0) ||
    !(earn.earnPoints > 0) ||
    !(redeem.redeemPointsCost > 0) ||
    !(redeem.redeemValue > 0)
  )
    return 0;
  const raw =
    (earn.earnPoints / earn.earnUnitAmount) *
    (redeem.redeemValue / redeem.redeemPointsCost) *
    100;
  return Math.round((raw + Number.EPSILON) * 10) / 10;
}

export interface IClubRedeemCaps {
  minRedeemPoints: number;
  maxRedeemPercent: number;
}

function round2(val: number): number {
  return Math.round((val + Number.EPSILON) * 100) / 100;
}

/**
 * Max points redeemable on an order, mirroring the backend clamp
 * (club-redeem.utils.ts): `maxRedeemPercent` over the pre-club-discount
 * order value (delivery and restaurant rounding excluded) minus existing
 * club discounts, the unpaid amount, and the member's balance. Returns 0
 * when nothing can be redeemed (`minRedeemPoints` is a separate
 * request-side check).
 */
export function maxRedeemablePoints(params: {
  program: IClubRedeemConfig & IClubRedeemCaps;
  orderTotal: number;
  /** `order.roundDifference` (rounded - unrounded total); 0 when unrounded. */
  roundDifference: number;
  deliveryCost: number;
  totalPaid: number;
  existingClubValue: number;
  memberPoints: number;
}): number {
  const { program } = params;
  if (!(program.redeemPointsCost > 0) || !(program.redeemValue > 0)) return 0;

  const unroundedTotal = round2(
    params.orderTotal - (params.roundDifference || 0)
  );
  const basis = round2(
    unroundedTotal + params.existingClubValue - (params.deliveryCost || 0)
  );
  const capTotal = round2((basis * program.maxRedeemPercent) / 100);
  const capRemaining = round2(capTotal - params.existingClubValue);
  const unpaid = round2(params.orderTotal - (params.totalPaid || 0));
  const maxValue = Math.min(capRemaining, unpaid);
  if (!(maxValue > 0)) return 0;

  const unitValue = program.redeemValue / program.redeemPointsCost;
  const maxPoints = Math.floor(maxValue / unitValue + 1e-9);
  return Math.max(0, Math.min(maxPoints, params.memberPoints));
}

/**
 * Quick-pick amounts for the redeem modal: up to three evenly spaced steps
 * below `maxPoints` (step = a quarter of the max, rounded UP to whole redeem
 * units), keeping only values at/above the program minimum and strictly
 * below the max, which is always appended last (the UI labels it "Todo").
 * Empty when nothing is redeemable.
 */
export function redeemPresetPoints(params: {
  maxPoints: number;
  minPoints: number;
  redeemPointsCost: number;
}): number[] {
  const { maxPoints } = params;
  if (!(maxPoints > 0)) return [];

  const unit = params.redeemPointsCost > 0 ? params.redeemPointsCost : 1;
  const floor = Math.max(params.minPoints, 1);
  const step = Math.ceil(maxPoints / 4 / unit) * unit;
  const presets: number[] = [];
  for (let points = step; points < maxPoints; points += step) {
    if (points >= floor) presets.push(points);
  }
  return presets.concat(maxPoints);
}

export interface IClubTopUpPlanLike {
  amount: number;
  bonus: number;
}

/**
 * Promotional bonus for a top-up amount. Mirrors the API's
 * `resolveTopupBonus` (club-topup-bonus.util.ts) — keep both in sync: plan
 * tiers act as thresholds, so a custom amount at or above a tier earns that
 * tier's bonus. Invalid plans (amount <= 0 or bonus < 0) are ignored; ties
 * on amount resolve to the highest bonus; 0 when no tier qualifies.
 */
export function topupBonusForAmount(
  plans: IClubTopUpPlanLike[] | null | undefined,
  principal: number
): number {
  if (!plans?.length) return 0;

  const target = round2(+principal);
  let best: { amount: number; bonus: number } | null = null;

  for (const plan of plans) {
    const amount = round2(+plan.amount);
    const bonus = round2(+(plan.bonus ?? 0));
    if (!(amount > 0) || bonus < 0) continue;
    if (amount > target) continue;
    if (
      !best ||
      amount > best.amount ||
      (amount === best.amount && bonus > best.bonus)
    ) {
      best = { amount, bonus };
    }
  }

  return best ? best.bonus : 0;
}

/** "123456" → "123 456" (display formatting for member codes). */
export function formatClubCode(code: string): string {
  const digits = String(code ?? '').replace(/\D+/g, '');
  if (digits.length !== 6) return code;
  return `${digits.slice(0, 3)} ${digits.slice(3)}`;
}

/**
 * Whether the program applies at a branch (null/empty branchIds = all
 * branches). Mirrors the backend eligibility rule in ClubEarnService.
 */
export function clubProgramCoversBranch(
  program: { branchIds: number[] | null },
  branchId: number | null | undefined
): boolean {
  const ids = program.branchIds;
  if (!ids || ids.length === 0) return true;
  return branchId != null && ids.includes(branchId);
}
